import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { dashApi } from "../api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing or invalid reset token.");
      return;
    }
    if (pw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await dashApi.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/dashboard/login", { replace: true }), 2500);
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-900 flex items-center justify-center p-4"
      data-testid="dashboard-reset-password-page"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-blue-600 mb-3">
            {done ? <CheckCircle2 className="h-6 w-6 text-white" /> : <KeyRound className="h-6 w-6 text-white" />}
          </div>
          <h1 className="text-2xl font-heading font-bold text-white">
            {done ? "Password updated" : "Choose a new password"}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {done ? "Redirecting you to sign in…" : "Pick something at least 8 characters long."}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
          {done ? (
            <div className="text-center" data-testid="reset-password-success">
              <Link
                to="/dashboard/login"
                className="text-sm text-blue-700 hover:underline"
              >
                Sign in now →
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="rp-new">New password</Label>
                <Input
                  id="rp-new"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password-new-input"
                />
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm new password</Label>
                <Input
                  id="rp-confirm"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && (
                <div
                  role="alert"
                  data-testid="reset-password-error"
                  className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-md"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !token}
                data-testid="reset-password-submit-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
